import Icon from '@components/common/Icon';

const SkillsAcquiredSection = () => {
  const skillCategories = [
    {
      category: 'Programming Languages',
      icon: 'laptop',
      skills: [
        { name: 'Python', level: 'Advanced' },
        { name: 'JavaScript', level: 'Intermediate' },
        { name: 'SQL', level: 'Intermediate' },
      ],
    },
    {
      category: 'AI & Machine Learning',
      icon: 'robot',
      skills: [
        { name: 'Large Language Models (LLMs)', level: 'Intermediate' },
        { name: 'RAG Architecture', level: 'Intermediate' },
        { name: 'Prompt Engineering', level: 'Advanced' },
        { name: 'Google AI Development Kit', level: 'Intermediate' },
      ],
    },
    {
      category: 'Development Tools',
      icon: 'tools',
      skills: [
        { name: 'Google APIs', level: 'Intermediate' },
        { name: 'Synthflow', level: 'Beginner' },
        { name: 'Make.com', level: 'Beginner' },
        { name: 'iOS Development', level: 'Intermediate' },
      ],
    },
    {
      category: 'Robotics & Automation',
      icon: 'robotics',
      skills: [
        { name: 'Industrial Robotics', level: 'Beginner' },
        { name: 'AI-Driven Automation', level: 'Intermediate' },
        { name: 'System Configuration', level: 'Beginner' },
        { name: 'Safety Protocols', level: 'Intermediate' },
      ],
    },
    {
      category: 'Professional Skills',
      icon: 'target',
      skills: [
        { name: 'Problem Solving', level: 'Advanced' },
        { name: 'Technical Communication', level: 'Advanced' },
        { name: 'Documentation', level: 'Advanced' },
        { name: 'Team Collaboration', level: 'Advanced' },
      ],
    },
    {
      category: 'Domain Knowledge',
      icon: 'books',
      skills: [
        { name: 'Retail Technology', level: 'Intermediate' },
        { name: 'Support Systems', level: 'Intermediate' },
        { name: 'Manufacturing Processes', level: 'Beginner' },
        { name: 'Quality Assurance', level: 'Intermediate' },
      ],
    },
  ];

  const getLevelColor = (level) => {
    switch (level) {
      case 'Advanced':
        return 'var(--color-accent-6)';
      case 'Intermediate':
        return 'var(--color-accent-5)';
      case 'Beginner':
        return 'var(--color-accent-4)';
      default:
        return 'var(--color-muted)';
    }
  };

  return (
    <section
      className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: 'var(--color-theme-2)' }}
    >
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-12 space-y-4">
          <h2
            className="text-4xl sm:text-5xl font-heading font-bold"
            style={{ color: 'var(--color-heading)' }}
          >
            Skills Acquired
          </h2>
          <p
            className="text-lg max-w-3xl mx-auto"
            style={{ color: 'var(--color-subtle)' }}
          >
            Technical competencies and professional skills developed through hands-on internship experiences
          </p>
        </div>

        {/* Skills Grid - Horizontal scroll on mobile, grid on desktop */}
        <div>
          {/* Mobile: Horizontal scroll */}
          <div className="md:hidden flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide">
            {skillCategories.map((category, idx) => (
              <div
                key={idx}
                className="rounded-xl p-6 transition-all duration-300 min-w-[280px] flex-shrink-0 snap-center"
                style={{
                  backgroundColor: 'var(--color-card-bg)',
                  borderColor: 'var(--color-card-border)',
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }}
              >
              {/* Category Header */}
              <div className="flex items-center space-x-3 mb-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{
                    backgroundColor: 'var(--color-theme-2)',
                    borderColor: 'var(--color-border-light)',
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  }}
                >
                  <Icon name={category.icon} size="xl" className="text-3xl" style={{ color: 'var(--color-accent-6)' }} />
                </div>
                <h3
                  className="font-semibold text-lg"
                  style={{ color: 'var(--color-heading)' }}
                >
                  {category.category}
                </h3>
              </div>

              {/* Skills List */}
              <div className="space-y-3">
                {category.skills.map((skill, skillIdx) => (
                  <div key={skillIdx} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span
                        className="text-sm font-medium"
                        style={{ color: 'var(--color-card-text)' }}
                      >
                        {skill.name}
                      </span>
                      <span
                        className="text-xs font-semibold px-2 py-0.5 rounded"
                        style={{
                          backgroundColor: 'var(--color-theme-2)',
                          color: getLevelColor(skill.level)
                        }}
                      >
                        {skill.level}
                      </span>
                    </div>
                    {/* Skill Level Bar */}
                    <div
                      className="h-1.5 rounded-full overflow-hidden"
                      style={{ backgroundColor: 'var(--color-theme-3)' }}
                    >
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: skill.level === 'Advanced' ? '100%' : skill.level === 'Intermediate' ? '66%' : '33%',
                          backgroundColor: getLevelColor(skill.level)
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          </div>

          {/* Desktop: Grid */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {skillCategories.map((category, idx) => (
              <div
                key={idx}
                className="rounded-xl p-6 transition-all duration-300 hover:shadow-card-hover"
                style={{
                  backgroundColor: 'var(--color-card-bg)',
                  borderColor: 'var(--color-card-border)',
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }}
              >
              {/* Category Header */}
              <div className="flex items-center space-x-3 mb-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{
                    backgroundColor: 'var(--color-theme-2)',
                    borderColor: 'var(--color-border-light)',
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  }}
                >
                  <Icon name={category.icon} size="xl" className="text-3xl" style={{ color: 'var(--color-accent-6)' }} />
                </div>
                <h3
                  className="font-semibold text-lg"
                  style={{ color: 'var(--color-heading)' }}
                >
                  {category.category}
                </h3>
              </div>

              {/* Skills List */}
              <div className="space-y-3">
                {category.skills.map((skill, skillIdx) => (
                  <div key={skillIdx} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span
                        className="text-sm font-medium"
                        style={{ color: 'var(--color-card-text)' }}
                      >
                        {skill.name}
                      </span>
                      <span
                        className="text-xs font-semibold px-2 py-0.5 rounded"
                        style={{
                          backgroundColor: 'var(--color-theme-2)',
                          color: getLevelColor(skill.level)
                        }}
                      >
                        {skill.level}
                      </span>
                    </div>
                    {/* Skill Level Bar */}
                    <div
                      className="h-1.5 rounded-full overflow-hidden"
                      style={{ backgroundColor: 'var(--color-theme-3)' }}
                    >
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: skill.level === 'Advanced' ? '100%' : skill.level === 'Intermediate' ? '66%' : '33%',
                          backgroundColor: getLevelColor(skill.level)
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center justify-center gap-6 mt-12">
          <div className="flex items-center space-x-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: 'var(--color-accent-6)' }}
            ></div>
            <span className="text-sm" style={{ color: 'var(--color-subtle)' }}>
              Advanced
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: 'var(--color-accent-5)' }}
            ></div>
            <span className="text-sm" style={{ color: 'var(--color-subtle)' }}>
              Intermediate
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: 'var(--color-accent-4)' }}
            ></div>
            <span className="text-sm" style={{ color: 'var(--color-subtle)' }}>
              Beginner
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SkillsAcquiredSection;
